import { Paragraph } from "@george-labs.com/design-system"
import {
  LoanCaseDTO,
  RealEstateCollateralDTO,
  RealEstateCollateralGroupDTO,
} from "@smartx/mikro-vt-client-api"
import React, { useCallback, useEffect, useState } from "react"
import { useTranslation } from "react-i18next"
import { useValueLists } from "../../../app/hooks"
import { getCollateralAddress } from "../../../functions/caseHelper"
import { CollapsibleComponent } from "../../../Shared"
import OverviewRealEstateOwners from "./OverviewRealEstateOwners"

interface OverviewRealEstateCollateralsProps {
  loanCaseData: LoanCaseDTO
}

const getCollateralKey = (
  collateral: RealEstateCollateralDTO,
  fallbackIdx: number,
): string => {
  const address = collateral?.realEstateAddresses?.find(
    (el) => el.addressType === "term_cim",
  )
  if (address) {
    return `${address.postalCode}-${address.city}-${address.street}-${address.houseNumber}`
  }
  return `collateral-${fallbackIdx}`
}

const getGroupKey = (
  group: RealEstateCollateralGroupDTO,
  fallbackIdx: number,
): string => {
  const master =
    group.realEstateCollaterals?.find((el) => el.master) ??
    group.realEstateCollaterals?.[0]
  return master
    ? `group-${getCollateralKey(master, fallbackIdx)}`
    : `group-${fallbackIdx}`
}

const OverviewRealEstateCollaterals: React.FC<
  OverviewRealEstateCollateralsProps
> = ({ loanCaseData }) => {
  const { t } = useTranslation()
  const valueLists = useValueLists()
  const [groups, setGroups] = useState([])
  const locationText = t("case.fields.realEstateAddress.location")

  useEffect(() => {
    setGroups(loanCaseData?.realEstateCollateralGroups)
  }, [loanCaseData])

  const getCollateralTitle = useCallback(
    (group: RealEstateCollateralGroupDTO) => {
      const master = group.hasMembers
        ? group.realEstateCollaterals?.find((el) => el.master)
        : group.realEstateCollaterals?.find((el) => !!el)

      return getCollateralAddress(master, valueLists, locationText)
    },
    [locationText, valueLists],
  )

  return (
    <>
      {!groups?.length && (
        <Paragraph className="text-center g-fg-ui-yellow p-3">
          {t("case.overview.warnings.noRealEstateCollaterals")}
        </Paragraph>
      )}

      {groups?.length > 0 &&
        groups?.map((group: RealEstateCollateralGroupDTO, gidx: number) => {
          return (
            <CollapsibleComponent
              key={getGroupKey(group, gidx)}
              titleString={getCollateralTitle(group)}
              content={
                <>
                  {group?.realEstateCollaterals?.map(
                    (collateral: RealEstateCollateralDTO, idx: number) => (
                      <React.Fragment key={getCollateralKey(collateral, idx)}>
                        {group.hasMembers && (
                          <CollapsibleComponent
                            titleString={getCollateralAddress(
                              collateral,
                              valueLists,
                              locationText,
                            )}
                            content={
                              <OverviewRealEstateOwners
                                realEstateOwners={collateral.realEstateOwners}
                              />
                            }
                          />
                        )}
                        {!group.hasMembers && (
                          <OverviewRealEstateOwners
                            realEstateOwners={collateral.realEstateOwners}
                          />
                        )}
                      </React.Fragment>
                    ),
                  )}
                </>
              }
            />
          )
        })}
    </>
  )
}

export default OverviewRealEstateCollaterals
